import * as React from "react";
import { useState } from "react";
import {
  Alert,
  Button,
  ColumnLayout,
  Container,
  ContentLayout,
  DatePicker,
  FormField,
  Header,
  KeyValuePairs,
  Link,
  Select,
  SpaceBetween,
  Table,
  Wizard,
} from "@amzn/awsui-components-react";
import Input from "@amzn/awsui-components-react/polaris/input";
import { OptionDefinition } from "@amzn/awsui-components-react/polaris/internal/components/option/interfaces";
import DarkModeContext from "../../contexes/DarkModeContext";
import { allMarketplacesMap, newUUID } from "../../helpers/RavenUtils";
import RecentTransactions from "../RecentTransactions";
import { Transaction } from "../../lusca-search-api/generated-src";
import { ORDER_VALIDATION_COLUMN_DEFINITIONS } from "./recentUATTableConfig";
import { GL_MAPPING } from "../../hooks/useSummaryData";
import {TestCase, UATProject, UATStatus} from "../../types/UATProject";

interface RavenWizardProps {
  onClose: () => void;
}

export default function RavenWizard({ onClose }: RavenWizardProps) {
  const [activeStepIndex, setActiveStepIndex] = React.useState(0);
  const [darkMode, setDarkMode] = useState(false);
  const [testName, setTestName] = React.useState("");
  const UATUUID = newUUID();
  const [owner, setOwner] = React.useState("");
  const [GLPG, setGLPG] = React.useState<OptionDefinition>({
    label: "TVOD (318)",
    value: "318",
  });
  const [marketplace, setMarketplace] = React.useState<OptionDefinition>({
    label: "United States",
    value: "US",
  });
  const [dueDate, setDueDate] = React.useState<string>(
    new Date().toDateString()
  );
  const [testDescription, setTestDescription] = React.useState("");
  const [IDs, setIDs] = React.useState<Array<Transaction>>([]);
  const [isComplete, setIsComplete] = useState(true);

  const handleRemoveTransaction = (transactionId: string) => {
    setIDs((prevIDs) =>
      prevIDs.filter((transaction) => transaction.id !== transactionId)
    );
  };

  const validateStep = (stepIndex: number) => {
    switch (stepIndex) {
      case 0:
        const complete =
          testName.trim() !== "" &&
          UATUUID !== "" &&
          GLPG !== null &&
          marketplace !== null &&
          testDescription.trim() !== "" &&
          owner.trim() !== "";
        setIsComplete(complete);
        return complete;
      case 1:
      case 2:
        return IDs.length > 0;
    }
  };

  const handleCreateUAT = () => {

    const testcases: TestCase[] = IDs.map((transaction) => ({
      name: `Test Case - ${transaction.usecase}`,
      status: UATStatus.PENDING,
      order: transaction,
      history: [],
      comments: []
    } as unknown as TestCase));

    const uatData: UATProject = {
      id: UATUUID,
      name: testName,
      GLPG: GLPG.value || "",
      marketplace: marketplace.value || "",
      description: testDescription,
      orderIDs: IDs,
      status: UATStatus.PENDING,
      created_at: new Date(),
      updated_at: new Date(),
      due_date: new Date(dueDate),
      owner,
      testCases: testcases,
    };
    const existingUATs = JSON.parse(localStorage.getItem("UAT") || "[]");
    localStorage.setItem("UAT", JSON.stringify([...existingUATs, uatData]));
    onClose();
  };

  return (
    <DarkModeContext.Provider value={darkMode}>
      <SpaceBetween size="xl">
        <ContentLayout defaultPadding>
          <Wizard
            i18nStrings={{
              navigationAriaLabel: "Steps",
              cancelButton: "Cancel",
              previousButton: "Previous",
              nextButton: "Next",
              optional: "optional",
            }}
            onNavigate={({ detail }) => {
              if (detail.requestedStepIndex > activeStepIndex) {
                if (!validateStep(activeStepIndex)) return;
              }
              setActiveStepIndex(detail.requestedStepIndex);
            }}
            onCancel={() => {
              onClose();
            }}
            onSubmit={() => handleCreateUAT()}
            activeStepIndex={activeStepIndex}
            submitButtonText="Create UAT"
            steps={[
              {
                title: "Basic information",
                description:
                  "Provide the basic information for this UAT project",
                content: (
                  <Container>
                    <SpaceBetween direction="vertical" size="l">
                      <ColumnLayout columns={2}>
                        <FormField label="Name">
                          <Input
                            ariaRequired
                            onChange={({ detail }) => setTestName(detail.value)}
                            value={testName}
                          />
                        </FormField>
                        <FormField label="Description">
                          <Input
                            ariaRequired
                            onChange={({ detail }) =>
                              setTestDescription(detail.value)
                            }
                            value={testDescription}
                          />
                        </FormField>
                        <FormField label="Owner">
                          <Input
                            ariaRequired
                            onChange={({ detail }) => setOwner(detail.value)}
                            value={owner}
                          />
                        </FormField>
                        <FormField label="GLPG">
                          <Select
                            selectedOption={GLPG}
                            onChange={({ detail }) =>
                              setGLPG(detail.selectedOption)
                            }
                            options={[
                              { label: "TVOD (318)", value: "318" },
                              { label: "Channels (613)", value: "613" },
                              { label: "SVOD (628)", value: "628" },
                              { label: "Addons (796)", value: "796" },
                            ]}
                          />
                        </FormField>
                        <FormField label="Marketplace">
                          <Select
                            selectedOption={marketplace}
                            onChange={({ detail }) =>
                              setMarketplace(detail.selectedOption)
                            }
                            options={allMarketplacesMap()}
                          />
                        </FormField>
                        <FormField
                          label="UAT Due Date"
                          constraintText="Use YYYY/MM/DD format."
                        >
                          <DatePicker
                            onChange={({ detail }) => setDueDate(detail.value)}
                            value={dueDate}
                            openCalendarAriaLabel={(selectedDate) =>
                              `Choose certificate expiry date${
                                selectedDate
                                  ? `, selected date is ${selectedDate}`
                                  : ""
                              }`
                            }
                            placeholder="YYYY/MM/DD"
                          />
                        </FormField>
                      </ColumnLayout>
                      {!isComplete && (
                        <Alert type="error" header="Incomplete page">
                          Please complete all fields before moving to the next
                          page.
                        </Alert>
                      )}
                    </SpaceBetween>
                  </Container>
                ),
              },
              {
                title: "Collate Orders",
                description:
                  "Provide order ID's or select from the transaction search below",
                content: (
                  <Container>
                    <SpaceBetween direction="vertical" size="l">
                      <RecentTransactions
                        isRaven
                        selectedOrders={IDs}
                        setSelectedOrders={setIDs}
                      />
                      {!isComplete && (
                        <Alert type="error" header="Incomplete page">
                          Please complete all fields before moving to the next
                          page.
                        </Alert>
                      )}
                    </SpaceBetween>
                  </Container>
                ),
              },
              {
                title: "Validate Orders",
                content: (
                  <Container>
                    <SpaceBetween direction="vertical" size="l">
                      <Table
                        items={IDs}
                        columnDefinitions={[
                          {
                            id: "id",
                            header: "UAT ID",
                            width: 350,
                            cell: (item: Transaction) => (
                              <Link
                                href={`?stage=${encodeURIComponent(
                                  item.stage
                                )}&transactionId=${encodeURIComponent(
                                  item.id
                                )}`}
                              >
                                {item.id}
                              </Link>
                            ),
                            sortingField: "id",
                          },
                          {
                            id: "country",
                            header: "Country",
                            cell: (item: Transaction) => item.country,
                            sortingField: "country",
                          },
                          {
                            id: "gl",
                            header: "GL",
                            cell: (item: Transaction) =>
                              `${GL_MAPPING[item.gl] || item.gl} (${item.gl})`,
                            sortingField: "gl",
                          },
                          {
                            id: "usecase",
                            header: "Usecase",
                            width: 200,
                            cell: (item: Transaction) => item.usecase,
                            sortingField: "usecase",
                          },
                          {
                            id: "status",
                            header: "Status",
                            width: 100,
                            cell: (item: Transaction) => {
                              item.usecase;
                            },
                            sortingField: "status",
                          },
                          {
                            id: "actions",
                            header: "Actions",
                            cell: (item) => (
                              <Button
                                variant="inline-link"
                                ariaLabel="Remove"
                                onClick={() => handleRemoveTransaction(item.id)}
                              >
                                Remove
                              </Button>
                            ),
                            minWidth: 170,
                          },
                        ]}
                      />
                    </SpaceBetween>
                  </Container>
                ),
              },
              {
                title: "Review and launch",
                content: (
                  <SpaceBetween size="xs">
                    <Header
                      variant="h3"
                      actions={
                        <Button onClick={() => setActiveStepIndex(0)}>
                          Edit
                        </Button>
                      }
                    />
                    <Container>
                      <KeyValuePairs
                        columns={2}
                        items={[
                          {
                            label: "UAT Name",
                            value: testName,
                          },
                          {
                            label: "UAT ID",
                            value: UATUUID,
                          },
                          {
                            label: "UAT Description",
                            value: testDescription,
                          },
                          {
                            label: "Owner",
                            value: owner,
                          },
                          {
                            label: "GLPG",
                            value: GLPG.value,
                          },
                          {
                            label: "Marketplace",
                            value: marketplace.value,
                          },
                        ]}
                      />
                      <Table
                        items={IDs}
                        columnDefinitions={ORDER_VALIDATION_COLUMN_DEFINITIONS}
                      />
                    </Container>
                  </SpaceBetween>
                ),
              },
            ]}
          />
        </ContentLayout>
      </SpaceBetween>
    </DarkModeContext.Provider>
  );
}
